import {
	Alert,
	Box,
	Checkbox,
	createStyles,
	Group,
	Select,
	SimpleGrid,
	Stack,
	Textarea,
	TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useScrollIntoView } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type { LiteWorkspace } from '@repo/api-codegen';
import { Button, Icon, Title } from '@repo/foundations';
import { has, isArray, isEmpty, isNil, set, update } from 'lodash-es';
import { useLayoutEffect, useMemo, useState } from 'react';
import { JSONTree } from 'react-json-tree';
import WorkspaceSearch from './components/WorkspaceSearch';
import { ACTION_FIELDS } from './constants';
import { ACTION_TYPE, type GroupedActionFields } from './types';
import { getInitialFormValues, onQueryAction } from './utils';

// Regular expression for validating domain names

interface IActionsPageProps {
	tabKey: string;
	type: ACTION_TYPE;
	onTabNameChange: (newName: string) => void;
}

const useStyles = createStyles((theme) => ({
	jsonTreeWrapper: {
		'ul:first-of-type ': {
			padding: `${theme.spacing.lg} !important`,
			borderRadius: theme.radius.lg,
		},
	},
}));

function ActionPage({ tabKey, type, onTabNameChange }: IActionsPageProps) {
	const { classes, theme } = useStyles();

	const { scrollIntoView, targetRef } = useScrollIntoView<HTMLDivElement>({
		offset: 60,
	});

	const form = useForm({
		initialValues: getInitialFormValues(type),
	});

	useLayoutEffect(() => {
		const storedValues = localStorage.getItem(
			`secoda-internal-query-action-page-${tabKey}`
		);
		if (!isNil(storedValues)) {
			form.setValues(JSON.parse(storedValues));
		}
	}, []);

	useLayoutEffect(() => {
		localStorage.setItem(
			`secoda-internal-query-action-page-${tabKey}`,
			JSON.stringify(form.values)
		);
	}, [form.values]);

	const [results, setResults] = useState<never>();
	const [error, setError] = useState<string | null>(null);
	const [workspaceSearchTerm, setWorkspaceSearchTerm] = useState('');

	const fields = useMemo(() => {
		const groupedFields: GroupedActionFields = ACTION_FIELDS[type].reduce(
			(acc, field) => {
				const groupName = field.group || 'UNGROUPED';

				if (!has(acc, groupName)) {
					set(acc, groupName, { string: [], boolean: [], select: [] });
				}

				const fieldTypePath = `${groupName}.${field.type}`;
				update(acc, fieldTypePath, (fs) => [...fs, field]);

				return acc;
			},
			{}
		);

		return groupedFields;
	}, [type]);

	const handleQuery = async () => {
		try {
			let data = await onQueryAction(type, form.values);

			// Add the updated property to the response data
			if (type === ACTION_TYPE.UPDATE_ALLOWED_DOMAINS && data) {
				// If data is already an object, enhance it; otherwise, create a new object
				const enhancedData = typeof data === 'object' ? { ...data } : {};

				// parse and format domains
				const allowedDomains = form.values.allowed_domains as string;
				const formattedDomains = allowedDomains
					? allowedDomains
							.split(',')
							.map((domain) => domain.trim())
							.filter(Boolean)
					: [];

				// Add the allowed_domains to the response
				enhancedData.allowed_domains = formattedDomains;

				data = enhancedData;
			} else if (type === ACTION_TYPE.UPDATE_DISABLED && data) {
				// If data is already an object, enhance it; otherwise, create a new object
				const enchancedData = typeof data === 'object' ? { ...data } : {};

				// Add the disabled status to the response
				enchancedData.disabled = form.values.disabled;

				data = enchancedData;
			}

			setResults(data as never);
			setError(null);

			if (!isNil(data)) {
				scrollIntoView();

				// Show success notification for workspace actions
				if (type === ACTION_TYPE.UPDATE_ALLOWED_DOMAINS) {
					showNotification({
						title: 'Sucess',
						message: `Workspace allowed domains have been updated successfully`,
						color: 'green',
					});
				} else if (type === ACTION_TYPE.UPDATE_DISABLED) {
					const disabledStatus = form.values.disabled ? 'disabled' : 'enabled';
					showNotification({
						title: 'Sucess',
						message: `Workspace has been ${disabledStatus} successfully`,
						color: 'green',
					});
				} else if (type === ACTION_TYPE.REGENERATE_WORKSPACE_POLICIES) {
					const rebuildType = form.values.complete_rebuild
						? 'complete'
						: 'partial';
					showNotification({
						title: 'Success',
						message: `Workspace policies ${rebuildType} regeneration completed successfully`,
						color: 'green',
					});
				} else if (type === ACTION_TYPE.UPDATE_WORKSPACE_QUALITY_FLAGS) {
					showNotification({
						title: 'Success',
						message:
							'Workspace quality flags update has been queued successfully',
						color: 'green',
					});
				} else if (type === ACTION_TYPE.RESET_DQS2_POLICIES) {
					showNotification({
						title: 'Success',
						message: 'Workspace DQS2 policies have been reset successfully',
						color: 'green',
					});
				} else if (type === ACTION_TYPE.TEST_REVERSE_TUNNEL) {
					showNotification({
						title: 'Success',
						message: 'Tunnel connectivity test has been initiated successfully',
						color: 'green',
					});
				} else if (type === ACTION_TYPE.TEST_CONNECTIVITY) {
					showNotification({
						title: 'Success',
						message:
							'Integration connectivity test has been initiated successfully',
						color: 'green',
					});
				} else if (type === ACTION_TYPE.BULK_EXPORT) {
					showNotification({
						title: 'Success',
						message: 'Bulk export has been queued successfully',
						color: 'green',
					});
				} else if (type === ACTION_TYPE.REMOVE_DISABLED_USERS_FROM_TEAMS) {
					showNotification({
						title: 'Success',
						message: 'Disabled user memberships have been removed successfully',
						color: 'green',
					});
				}
			}
		} catch (error: any) {
			setError(error?.message ?? JSON.stringify(error) ?? 'An error occurred');
		}
	};

	const handleResetForm = () => {
		form.reset();
		setResults(undefined);
		setError(null);
		setWorkspaceSearchTerm('');
	};

	const handleWorkspaceSelect = (workspace: LiteWorkspace) => {
		form.setFieldValue('name', workspace.name);
		form.setFieldValue('workspace_id', workspace.id);
		setWorkspaceSearchTerm('');
	};

	return (
		<Stack spacing={theme.spacing['5xl']} pt="xl">
			<Stack>
				{Object.keys(fields).map((group) => (
					<Stack key={group} spacing="sm">
						{group !== 'UNGROUPED' && <Title size="lg">{group}</Title>}
						<SimpleGrid cols={3} spacing="xl" verticalSpacing="xl">
							{fields[group].string.map((field) => {
								if (
									type === 'Get Workspace' &&
									field.value === 'workspace_id'
								) {
									return (
										<Box key={field.value}>
											<TextInput
												label={field.label}
												{...form.getInputProps(field.value)}
												onChange={(e) => {
													form.setFieldValue(field.value, e.target.value);
													setWorkspaceSearchTerm(e.target.value);
												}}
												value={
													(form.values[field.value] as string) ||
													workspaceSearchTerm
												}
												icon={<Icon name="search" size="md" />}
											/>
											{workspaceSearchTerm.length > 0 && (
												<Box mt="xs">
													<WorkspaceSearch
														searchTerm={workspaceSearchTerm}
														onChange={handleWorkspaceSelect}
													/>
												</Box>
											)}
										</Box>
									);
								}
								return field.display === 'textarea' ? (
									<Textarea
										key={field.value}
										label={field.label}
										autosize
										minRows={2}
										maxRows={15}
										{...form.getInputProps(field.value)}
									/>
								) : (
									<TextInput
										key={field.value}
										label={field.label}
										{...form.getInputProps(field.value)}
									/>
								);
							})}
							{fields[group].select.map((field) => (
								<Select
									key={field.value}
									label={field.label}
									data={field.options ?? []}
									{...form.getInputProps(field.value)}
								/>
							))}
						</SimpleGrid>
						<SimpleGrid cols={3} spacing="xl" verticalSpacing="xl">
							{fields[group].boolean.map((field) => (
								<Checkbox
									key={field.value}
									label={field.label}
									{...form.getInputProps(field.value, {
										type: 'checkbox',
									})}
								/>
							))}
						</SimpleGrid>
					</Stack>
				))}
			</Stack>
			<Group position="left" spacing={theme.spacing.md}>
				<Button variant="primary" size="lg" onClick={handleQuery}>
					Submit
				</Button>
				<Button variant="default" size="lg" onClick={handleResetForm}>
					Cancel
				</Button>
			</Group>
			{error && (
				<Alert title="Error" color="red">
					{error}
				</Alert>
			)}
			<Stack ref={targetRef}>
				{!isEmpty(results) &&
					isArray(results) &&
					(results as never[]).map((result) => (
						<Box
							className={classes.jsonTreeWrapper}
							key={JSON.stringify(result)}
						>
							<JSONTree data={result} />
						</Box>
					))}
				{!isNil(results) &&
					!isArray(results) &&
					(typeof results === 'object' ? (
						<Box className={classes.jsonTreeWrapper}>
							<JSONTree theme="tomorrow" data={results} />
						</Box>
					) : (
						<Title>{String(results)}</Title>
					))}
			</Stack>
		</Stack>
	);
}

export default ActionPage;
